import { useState, useEffect } from 'react';
import { Bell, X, Check, Package, Calendar, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import type { NotificationType, Notification } from '../hooks/useNotifications';

interface NotificationGroup {
  type: NotificationType;
  label: string;
  icon: string;
  color: string;
  count: number;
  notifications: Notification[];
}

interface NotificationCenterProps {
  groupedNotifications: NotificationGroup[];
  unreadCount: number;
  permissionGranted: boolean;
  onRequestPermission: () => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsReadByType: (type: NotificationType) => void;
  onClearRead: () => void;
}

// Icône selon le type
function TypeIcon({ type, size = 18 }: { type: NotificationType; size?: number }) {
  switch (type) {
    case 'material':
      return <Package size={size} />;
    case 'leave':
      return <Calendar size={size} />;
    case 'timesheet':
      return <Clock size={size} />;
    default:
      return <Bell size={size} />;
  }
}

// Formatage relatif du temps
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'À l\'instant';
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  return date.toLocaleDateString('fr-FR');
}

export default function NotificationCenter({
  groupedNotifications,
  unreadCount,
  permissionGranted,
  onRequestPermission,
  onMarkAsRead,
  onMarkAllAsReadByType,
  onClearRead
}: NotificationCenterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState<Set<NotificationType>>(new Set());

  // Fermer le panneau si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.notification-center')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  const toggleExpand = (type: NotificationType) => {
    setExpandedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const hasNotifications = groupedNotifications.length > 0;

  return (
    <div className="notification-center" style={{ position: 'relative' }}>
      {/* Bouton cloche */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        style={{
          position: 'relative',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        title="Notifications"
      >
        <Bell size={22} color="#64748b" />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              background: '#ef4444',
              color: 'white',
              fontSize: '11px',
              fontWeight: '700',
              borderRadius: '10px',
              minWidth: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px'
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Panneau des notifications */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '8px',
            width: '360px',
            maxHeight: '480px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            zIndex: 1000
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '16px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
              Notifications
              {unreadCount > 0 && (
                <span
                  style={{
                    marginLeft: '8px',
                    background: '#fee2e2',
                    color: '#dc2626',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '12px'
                  }}
                >
                  {unreadCount} nouvelle{unreadCount > 1 ? 's' : ''}
                </span>
              )}
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex'
              }}
            >
              <X size={18} color="#64748b" />
            </button>
          </div>

          {/* Permission banner */}
          {!permissionGranted && (
            <div
              style={{
                padding: '12px 16px',
                background: '#fffbeb',
                borderBottom: '1px solid #fcd34d',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <span style={{ fontSize: '13px', color: '#92400e', flex: 1 }}>
                Activez les notifications pour être alerté
              </span>
              <button
                onClick={onRequestPermission}
                style={{
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Activer
              </button>
            </div>
          )}

          {/* Contenu */}
          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            {!hasNotifications ? (
              <div
                style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: '#94a3b8'
                }}
              >
                <Bell size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: '14px' }}>Aucune notification</p>
              </div>
            ) : (
              groupedNotifications.map((group) => (
                <div key={group.type}>
                  {/* Header du groupe */}
                  <button
                    onClick={() => toggleExpand(group.type)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: '#f8fafc',
                      border: 'none',
                      borderBottom: '1px solid #e2e8f0',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      textAlign: 'left'
                    }}
                  >
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: `${group.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: group.color
                      }}
                    >
                      <TypeIcon type={group.type} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
                        {group.label}
                      </span>
                      {group.count > 0 && (
                        <span
                          style={{
                            marginLeft: '8px',
                            background: group.color,
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: '700'
                          }}
                        >
                          {group.count}
                        </span>
                      )}
                    </div>
                    {expandedTypes.has(group.type) ? (
                      <ChevronUp size={18} color="#94a3b8" />
                    ) : (
                      <ChevronDown size={18} color="#94a3b8" />
                    )}
                  </button>

                  {/* Liste des notifications du groupe */}
                  {expandedTypes.has(group.type) && (
                    <div>
                      {/* Actions groupées */}
                      {group.count > 0 && (
                        <button
                          onClick={() => onMarkAllAsReadByType(group.type)}
                          style={{
                            width: '100%',
                            padding: '8px 16px',
                            background: 'transparent',
                            border: 'none',
                            borderBottom: '1px solid #f1f5f9',
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: '#64748b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <Check size={14} /> Tout marquer comme lu
                        </button>
                      )}

                      {/* Notifications individuelles */}
                      {group.notifications.map((notification) => (
                        <div
                          key={notification.id}
                          onClick={() => onMarkAsRead(notification.id)}
                          style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #f1f5f9',
                            cursor: 'pointer',
                            background: notification.read ? 'transparent' : '#f0f9ff',
                            transition: 'background 0.2s'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: '8px'
                            }}
                          >
                            <p
                              style={{
                                margin: 0,
                                fontSize: '13px',
                                color: '#334155',
                                lineHeight: '1.4',
                                flex: 1
                              }}
                            >
                              {notification.message}
                            </p>
                            {!notification.read && (
                              <span
                                style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  background: group.color,
                                  flexShrink: 0,
                                  marginTop: '4px'
                                }}
                              />
                            )}
                          </div>
                          <span
                            style={{
                              fontSize: '11px',
                              color: '#94a3b8',
                              marginTop: '4px',
                              display: 'block'
                            }}
                          >
                            {formatRelativeTime(notification.timestamp)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {hasNotifications && (
            <div
              style={{
                padding: '12px 16px',
                borderTop: '1px solid #e2e8f0',
                textAlign: 'center'
              }}
            >
              <button
                onClick={onClearRead}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  fontSize: '13px',
                  cursor: 'pointer',
                  padding: '4px 8px'
                }}
              >
                Effacer les notifications lues
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
